import type { MediaKind } from '../types';

// Media-type inference (doc 09 §4.9 / §8 risk #9). Feedback attachments carry no typed field, so we
// infer by extension; content media prefer the explicit fileType and only fall back to the extension.

const VIDEO_EXT = ['.mp4', '.mov', '.webm', '.m3u8', '.avi', '.mkv', '.m4v'];
const IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.heic', '.avif'];
const AUDIO_EXT = ['.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a'];

/** Strip query string, lower-case, and test the path suffix against an extension set. */
function hasExt(url: string, exts: string[]): boolean {
  const path = url.split('?')[0]?.toLowerCase() ?? '';
  return exts.some((e) => path.endsWith(e)) || (url.includes('.m3u8') && exts.includes('.m3u8'));
}

export function isVideoUrl(url: string | null | undefined): boolean {
  return !!url && hasExt(url, VIDEO_EXT);
}

export function isImageUrl(url: string | null | undefined): boolean {
  return !!url && hasExt(url, IMAGE_EXT);
}

export function isAudioUrl(url: string | null | undefined): boolean {
  return !!url && hasExt(url, AUDIO_EXT);
}

/** HLS video streams are served as .m3u8 and need the Mux player rather than a raw <video>. */
export function isHlsUrl(url: string | null | undefined): boolean {
  return !!url && url.includes('.m3u8');
}

/**
 * Resolve a media kind, preferring the backend fileType code (VID/VIDEO, IMG/IMAGE, AUD/AUDIO) and
 * falling back to URL-extension sniffing. Defaults to IMG (safe to render in an <img>).
 */
export function resolveMediaKind(
  fileType: string | null | undefined,
  url: string | null | undefined,
): MediaKind {
  const t = (fileType ?? '').trim().toUpperCase();
  if (t.startsWith('VID')) return 'VID';
  if (t.startsWith('IMG') || t.startsWith('IMA') || t === 'PHOTO') return 'IMG';
  if (t.startsWith('AUD')) return 'AUD';
  if (isVideoUrl(url)) return 'VID';
  if (isAudioUrl(url)) return 'AUD';
  return 'IMG';
}
